import {
  Mark,
  getMarkAttributes,
  type Command,
  type Editor,
} from "@tiptap/core";
import type { MarkType } from "@tiptap/pm/model";
import {
  INLINE_TEXT_SPAN_COLOR_PROPERTY,
  INLINE_TEXT_SPAN_SIZE_PROPERTY,
  inlineTextSpanStyleText,
  mergeInlineTextSpanAttributes,
  readInlineTextSpanAttributes,
  type InlineTextSpanPatch,
} from "@/components/inlineTextSpan";
import { TEXT_STYLE_MARK_NAME } from "./textEditModel";

/** One span attribute the toolbar reads and writes per selection. */
export type TextStyleAttribute = "size" | "color";

/** What one span attribute reads as across the current selection. */
export type TextStyleSelection =
  | { state: "uniform"; value: string | null }
  | { state: "mixed" };

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    textStyle: {
      /** Sets the selection's own font size, or clears it with null. */
      setTextStyleSize: (size: string | null) => ReturnType;
      /** Sets the selection's own colour, or clears it with null. */
      setTextStyleColor: (color: string | null) => ReturnType;
    };
  }
}

/**
 * Inline TipTap mark carrying one run's own font size and colour, committed to
 * the markdown body as a `<span size="..." color="...">` tag. The two parts are
 * independent: setting one preserves the other, and clearing the last one drops
 * the mark rather than leaving an empty span behind.
 */
export const TextStyleMark = Mark.create({
  name: TEXT_STYLE_MARK_NAME,

  addAttributes() {
    return {
      size: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute(INLINE_TEXT_SPAN_SIZE_PROPERTY),
        rendered: false,
      },
      color: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute(INLINE_TEXT_SPAN_COLOR_PROPERTY),
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [
      { tag: `span[${INLINE_TEXT_SPAN_SIZE_PROPERTY}]` },
      { tag: `span[${INLINE_TEXT_SPAN_COLOR_PROPERTY}]` },
    ];
  },

  renderHTML({ mark }) {
    const attributes = readInlineTextSpanAttributes(mark.attrs);
    if (attributes === null) {
      return ["span", 0];
    }
    return [
      "span",
      {
        style: inlineTextSpanStyleText(attributes),
        [INLINE_TEXT_SPAN_SIZE_PROPERTY]: attributes.size,
        [INLINE_TEXT_SPAN_COLOR_PROPERTY]: attributes.color,
      },
      0,
    ];
  },

  addCommands() {
    const type = this.type;
    return {
      setTextStyleSize: (size) => patchTextStyle(type, { size }),
      setTextStyleColor: (color) => patchTextStyle(type, { color }),
    };
  },
});

/**
 * Reads what one span attribute is across the selection: a single value (or
 * none) when every run agrees, "mixed" otherwise.
 * @param editor - The open session's TipTap instance
 * @param attribute - Which span attribute to read
 * @returns The selection's value for that attribute
 */
export function readTextStyleSelection(
  editor: Editor,
  attribute: TextStyleAttribute,
): TextStyleSelection {
  const { empty, from, to } = editor.state.selection;
  if (empty) {
    const attributes = readInlineTextSpanAttributes(
      getMarkAttributes(editor.state, TEXT_STYLE_MARK_NAME),
    );
    return { state: "uniform", value: attributes?.[attribute] ?? null };
  }
  const values: (string | null)[] = [];
  editor.state.doc.nodesBetween(from, to, (node) => {
    if (!node.isInline) {
      return;
    }
    const mark = node.marks.find(
      (candidate) => candidate.type.name === TEXT_STYLE_MARK_NAME,
    );
    const attributes = mark ? readInlineTextSpanAttributes(mark.attrs) : null;
    values.push(attributes?.[attribute] ?? null);
  });
  const [first, ...rest] = values;
  if (values.length === 0) {
    return { state: "uniform", value: null };
  }
  return rest.every((value) => value === first)
    ? { state: "uniform", value: first }
    : { state: "mixed" };
}

/**
 * The style the browser actually paints the selection with, which is what an
 * unstyled run inherits from the block.
 * @param editor - The open session's TipTap instance
 * @param property - The CSS property to read
 * @returns The computed value, or "" when the selection can't be measured
 */
export function computedSelectionStyle(
  editor: Editor,
  property: "font-size" | "color",
): string {
  if (editor.isDestroyed || !editor.view.dom.isConnected) {
    return "";
  }
  const { node } = editor.view.domAtPos(editor.state.selection.from);
  const element = node instanceof Element ? node : node.parentElement;
  return element === null
    ? ""
    : window.getComputedStyle(element).getPropertyValue(property);
}

/**
 * Applies a patch to the textStyle mark over the selection, merging with what
 * each run already carries and removing the mark from runs the patch empties.
 * @param type - The textStyle mark type
 * @param patch - The parts to set (a value) or clear (null)
 * @returns The TipTap command
 */
function patchTextStyle(type: MarkType, patch: InlineTextSpanPatch): Command {
  return ({ tr, state, dispatch }) => {
    if (!dispatch) {
      return true;
    }
    const { empty, ranges } = tr.selection;
    if (empty) {
      const merged = mergeInlineTextSpanAttributes(
        readInlineTextSpanAttributes(getMarkAttributes(state, type)),
        patch,
      );
      tr.removeStoredMark(type);
      if (merged !== null) {
        tr.addStoredMark(type.create({ ...merged }));
      }
      return true;
    }
    ranges.forEach(({ $from, $to }) => {
      const from = $from.pos;
      const to = $to.pos;
      state.doc.nodesBetween(from, to, (node, pos) => {
        if (!node.isInline) {
          return;
        }
        const start = Math.max(pos, from);
        const end = Math.min(pos + node.nodeSize, to);
        const existing = node.marks.find((mark) => mark.type === type);
        const merged = mergeInlineTextSpanAttributes(
          existing ? readInlineTextSpanAttributes(existing.attrs) : null,
          patch,
        );
        if (merged !== null) {
          tr.addMark(start, end, type.create({ ...merged }));
          return;
        }
        if (existing) {
          tr.removeMark(start, end, type);
        }
      });
    });
    return true;
  };
}
